import { useEffect, useState } from 'react'

/* A keyboard takes well over a fifth of a handheld screen; a shorter drop is the
   window settling (e.g. a status bar change), not a keyboard. */
const KEYBOARD_HEIGHT_RATIO = 0.8

/**
 * Whether the on-screen keyboard is up, read off the window shrinking.
 *
 * The Android shell resizes the WebView for the keyboard, so `window.innerHeight` drops
 * while it is open -- including when the focused field is inside a tool iframe, where no
 * focus event reaches this window. The tallest height seen at the current width is the
 * keyboard-closed height; keying it on width means a rotation starts a fresh baseline
 * rather than reading as a keyboard.
 *
 * iOS overlays the keyboard without resizing the window, so this stays false there.
 */
export const useSoftKeyboardOpen = (): boolean => {
    const [isOpen, setOpen] = useState(false)

    useEffect(() => {
        let baselineWidth = window.innerWidth
        let baselineHeight = window.innerHeight

        const onResize = () => {
            if (window.innerWidth !== baselineWidth) {
                baselineWidth = window.innerWidth
                baselineHeight = window.innerHeight
            } else {
                baselineHeight = Math.max(baselineHeight, window.innerHeight)
            }
            setOpen(window.innerHeight < baselineHeight * KEYBOARD_HEIGHT_RATIO)
        }

        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    return isOpen
}
